import React, {useMemo, useState} from 'react';
import Forms from "./Forms";
import Error from "./Error";
import API from "../utils/API";
import {API_ENDPOINTS} from "../apiConfig";

const SaleMarkSoldModal = ({sale, close, onSaved}) => {
    const [form, setForm] = useState({
        salePrice: sale?.salePrice ?? '',
        saleDate: sale?.saleDate || new Date().toISOString().slice(0, 10),
        buyerName: sale?.buyer?.name || '',
        buyerSurname: sale?.buyer?.surname || '',
        buyerEmail: sale?.buyer?.email || '',
        buyerPhone: sale?.buyer?.phone || ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const itemLabel = useMemo(() => {
        if (sale?.itemType === 'sparePart') {
            return sale?.part?.name || 'Náhradní díl';
        }
        return `${sale?.device?.name || 'Zařízení'}${sale?.device?.code ? ` (${sale.device.code})` : ''}`;
    }, [sale]);
    const purchasePrice = useMemo(() => {
        const value = Number(sale?.purchasePrice);
        return Number.isFinite(value) ? value : 0;
    }, [sale?.purchasePrice]);
    const repairCost = useMemo(() => {
        const value = Number(sale?.repairCost);
        return Number.isFinite(value) ? value : 0;
    }, [sale?.repairCost]);
    const liveProfit = useMemo(() => {
        const salePrice = Number(form.salePrice);
        if (!Number.isFinite(salePrice)) return null;
        return salePrice - purchasePrice - repairCost;
    }, [form.salePrice, purchasePrice, repairCost]);

    const setField = (key, value) => setForm((prev) => ({...prev, [key]: value}));

    const onSubmit = async (e) => {
        e?.preventDefault();
        setSaving(true);
        setError(null);
        try {
            await API.put(API_ENDPOINTS.sales.markSold(sale._id), {
                salePrice: form.salePrice,
                saleDate: form.saleDate,
                buyer: {
                    name: form.buyerName,
                    surname: form.buyerSurname,
                    email: form.buyerEmail,
                    phone: form.buyerPhone
                }
            });
            await onSaved?.();
            close?.(true);
        } catch (err) {
            setError(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            <div className="border rounded p-3 bg-gray-50 space-y-2">
                <div className="text-sm text-gray-700"><strong>Prodej:</strong> {sale?.saleNumber || '-'}</div>
                <div className="text-sm text-gray-700"><strong>Název:</strong> {itemLabel}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div className="text-sm text-gray-700"><strong>Cena výkupu:</strong> {formatMoney(purchasePrice)}</div>
                    <div className="text-sm text-gray-700"><strong>Cena opravy:</strong> {formatMoney(repairCost)}</div>
                    <div className="text-sm text-gray-700"><strong>Profit:</strong> {liveProfit === null ? '-' : formatMoney(liveProfit)}</div>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <Forms.Number
                    label="Prodejní cena"
                    value={form.salePrice}
                    onChange={(value) => setField('salePrice', value)}
                />
                <Forms.Date
                    label="Datum prodeje"
                    value={form.saleDate}
                    onChange={(value) => setField('saleDate', value || null)}
                />
                <Forms.Text
                    label="Jméno kupujícího"
                    value={form.buyerName}
                    onChange={(value) => setField('buyerName', value)}
                />
                <Forms.Text
                    label="Příjmení kupujícího"
                    value={form.buyerSurname}
                    onChange={(value) => setField('buyerSurname', value)}
                />
                <Forms.Email
                    label="Email kupujícího"
                    value={form.buyerEmail}
                    onChange={(value) => setField('buyerEmail', value)}
                />
                <Forms.Text
                    label="Telefon kupujícího"
                    value={form.buyerPhone}
                    onChange={(value) => setField('buyerPhone', value)}
                />
            </div>

            {error ? <Error error={error}/> : null}
            <Forms.Button submit label={saving ? 'Ukládám…' : 'Uložit jako prodáno'} disabled={saving}/>
        </form>
    );
};

export default SaleMarkSoldModal;

function formatMoney(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '-';
    return `${number.toLocaleString('cs-CZ')} CZK`;
}
